import { Link } from 'react-router-dom';
import { Heart, Eye, MapPin, Truck, CheckCircle, ShieldCheck } from 'lucide-react';
import { DemoBadge, DemoWatermark } from './WhatsAppButton';

export function ListingCard({ listing, onFavorite }) {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);
  };

  return (
    <Link 
      to={`/anuncio/${listing.id}`}
      className="group relative rounded-2xl overflow-hidden bg-[#121215] border border-white/5 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-15px_rgba(124,58,237,0.15)]"
      data-testid={`listing-card-${listing.id}`}
    >
      {/* Image */}
      <div className="aspect-square w-full overflow-hidden relative">
        {listing.is_demo && <DemoBadge />}
        {listing.is_demo && <DemoWatermark />}
        {listing.images && listing.images.length > 0 ? (
          <img 
            src={listing.images[0]} 
            alt={listing.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full bg-zinc-800 flex items-center justify-center">
            <span className="text-zinc-600">Sin imagen</span>
          </div>
        )}
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex gap-2 z-10">
          {listing.is_premium && (
            <span className="bg-purple-500/20 text-purple-300 border border-purple-500/30 backdrop-blur-md rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              Premium
            </span>
          )}
          {listing.is_featured && (
            <span className="bg-blue-500/20 text-blue-300 border border-blue-500/30 backdrop-blur-md rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              Destacado
            </span>
          )}
        </div>

        {/* Favorite Button */}
        {onFavorite && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onFavorite(listing.id);
            }}
            className={`absolute top-3 right-3 p-2 rounded-full backdrop-blur-md transition-all ${
              listing.is_favorited 
                ? 'bg-red-500/20 text-red-400' 
                : 'bg-black/30 text-white/70 hover:text-white'
            }`}
            data-testid={`favorite-btn-${listing.id}`}
          >
            <Heart className={`w-4 h-4 ${listing.is_favorited ? 'fill-current' : ''}`} />
          </button>
        )}

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#121215] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium text-zinc-100 line-clamp-1 group-hover:text-white transition-colors">
            {listing.title}
          </h3>
        </div>

        <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
          {listing.category_name && (
            <span className="bg-white/5 px-2 py-0.5 rounded-full">{listing.category_name}</span>
          )}
          {listing.sex && (
            <span className={`px-2 py-0.5 rounded-full ${
              listing.sex === 'macho' ? 'bg-blue-500/10 text-blue-400' : 
              listing.sex === 'hembra' ? 'bg-pink-500/10 text-pink-400' : 
              'bg-white/5'
            }`}>
              {listing.sex}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-zinc-100 font-[Outfit]">
            {formatPrice(listing.price)}
          </span>
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              {listing.views || 0}
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-3.5 h-3.5" />
              {listing.favorites_count || 0}
            </span>
          </div>
        </div>

        {/* Seller info */}
        {listing.seller_name && (
          <div className="mt-3 pt-3 border-t border-white/5 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-xs">
              {listing.seller_avatar ? (
                <img src={listing.seller_avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                listing.seller_name.charAt(0).toUpperCase()
              )}
            </div>
            <span className="text-xs text-zinc-500">{listing.seller_name}</span>
            {listing.seller_verified && (
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" title="Criador Verificado" />
            )}
            {listing.shipping_available && (
              <Truck className="w-3.5 h-3.5 text-emerald-500 ml-auto" />
            )}
          </div>
        )}
      </div>
    </Link>
  );
}

export function ListingCardSkeleton() {
  return (
    <div className="rounded-2xl overflow-hidden bg-[#121215] border border-white/5">
      <div className="aspect-square w-full skeleton" />
      <div className="p-4 space-y-3">
        <div className="h-5 w-3/4 skeleton rounded" />
        <div className="h-4 w-1/2 skeleton rounded" />
        <div className="h-6 w-1/3 skeleton rounded" />
      </div>
    </div>
  );
}
